<template>
  <!-- eslint-disable vue-a11y/label-has-for, vue-a11y/form-has-label, vue-a11y/no-onchange -->
  <ValidationProvider
    #default="{ classes, valid, validate, errors }"
    :disabled="!rules && !isRequired"
    :name="name"
    :rules="rules"
    slim>
    <div class="Field-cont" :class="{ 'has-errors': errors[0] }">
      <label v-if="(label || $slots.label) && !is('checkbox', 'radio')" :for="inputId">
        <slot name="label">
          {{ label }}
          <span v-if="isRequired" class="required">
            *
          </span>
        </slot>
      </label>
      <div class="Field" :class="[...fieldClasses, ...classes]">
        <Icon v-if="_icon" :name="_icon" />
        <input
          v-if="isInput"
          :id="inputId"
          v-bind="attrs"
          :type="_type"
          :value="_value"
          @change="change"
          @focus="$emit('focus')"
          @focusout="$emit('focusout')"
          @input="set">
        <div v-else-if="is('checkbox')" class="checkbox">
          <label :class="{ 'switch': isSwitch }" :for="inputId">
            <input
              :id="inputId"
              v-bind="attrs"
              :checked="value ? 'checked' : ''"
              type="checkbox"
              :value="value"
              @change="set($event.target.checked)">
            <template v-if="$slots.label">
              <slot name="label" />
            </template>
            <template v-else>
              {{ label }}
            </template>
            <div class="slider" />
          </label>
        </div>
        <textarea
          v-else-if="is('textarea')"
          :id="inputId"
          v-bind="attrs"
          :value="_value"
          @change="change"
          @focus="$emit('focus')"
          @focusout="$emit('focusout')"
          @input="set($event)" />
        <select
          v-else-if="is('select')"
          :id="inputId"
          v-bind="attrs"
          :value="_value"
          @blur="setSelectIcon('down')"
          @change="set($event)"
          @focus="setFocus(true)"
          @focusout="setFocus(false);setSelectIcon('down')"
          @mousedown="setSelectIcon('up')"
          @mouseup="setSelectIcon('down')">
          <option v-if="$attrs.placeholder" disabled :value="null">
            {{ $attrs.placeholder }}
          </option>
          <option
            v-for="opt in selectOptions"
            :key="`${opt[0]}`"
            :value="opt[0]">
            {{ opt[1] }}
          </option>
        </select>
        <slot />
      </div>
      <transition name="slide-fade">
        <div v-show="errors[0]" class="Field-errors">
          {{ errors[0] }}
        </div>
      </transition>
    </div>
  </ValidationProvider>
</template>

<script>
import { isNumeric, isObject, randomString } from '../../utils'

const filters = {
  numeric: {
    out: value => isNumeric(value) ? Number(value) : null,
  },
  select: {
    // TODO: generalize into an auto type casting function
    out: value => {
      if (value === null || value === '' || value === 'null') return null
      if (value === 'true') return true
      if (value === 'false') return false

      return isNaN(Number(value)) ? value : Number(value)
    },
  },
}

export default {
  props: {
    disabled: { type: Boolean, default: false },
    icon: String,
    label: String,
    name: String,
    isSwitch: { type: Boolean, default: false },
    options: Array,
    required: { type: Boolean, default: false },
    type: { type: String, default: 'text' },
    rules: [String, Object],
    value: { type: null },
  },
  data() {
    return {
      inputId: randomString(),
      isFocused: false,
      selectIcon: 'down',
    }
  },
  computed: {
    attrs() {
      return {
        ...this.$attrs,
        'aria-label': this.ariaLabel,
        disabled: this.disabled,
        name: this.name,
        required: this.isRequired,
      }
    },
    ariaLabel() {
      const label = this.label ||
        this.$attrs.placeholder ||
        this.$attrs.ariaLabel ||
        this.$attrs['aria-label']

      if (typeof label !== 'string') return

      return label.replace('*', '').trim()
    },
    _icon() {
      return this.icon || {
        money: 'eur',
        select: this.selectIcon,
      }[this.type]
    },
    _type() {
      return {
        money: 'number',
      }[this.type] || this.type
    },
    _value() {
      return this.filterIn
        ? this.filterIn(this.value)
        : this.value
    },
    fieldClasses() {
      const conditions = [
        [this.disabled, 'disabled'],
        [this.isRequired, 'required'],
        [this.isFocused, 'focus'],
        [this.is('select'), 'trans'],
        [this._icon, 'with-icon'],
        [this.isInput, 'inp'],
        [this.isIconRight, 'icon-right'],
      ]

      return [
        `type-${this._type}`,
        ...conditions
          .filter(([condition]) => condition)
          .map(([_, classes]) => classes),
      ]
    },
    filterIn() {
      return {
        // check: filters.check.in,
        // time: filters.time.in,
      }[this.type]
    },
    filterOut() {
      return {
        text: value => value || null,
        // check: filters.check.out,
        money: filters.numeric.out,
        number: filters.numeric.out,
        select: filters.select.out,
      }[this.type]
    },
    isIconRight() {
      return ['money'].includes(this.type)
    },
    isInput() {
      return ['email', 'hidden', 'number', 'password', 'percent', 'text'].includes(this._type)
    },
    isRequired() {
      return this.required !== false
    },
    selectOptions() {
      let options = []

      if (Array.isArray(this.options)) {
        if (this.options.length !== 0 && Array.isArray(this.options[0])) {
          // array of pairs [key, value]
          options = this.options
        } else {
          // array of values
          options = this.options.map(option => [option, option])
        }
      } else if (isObject(this.options)) {
        options = Object.keys(this.options).map(key => [key, this.options[key]])
      }

      // return array of pairs to ensure the order of elements
      return options
    },
  },
  methods: {
    change(event) {
      this.$emit('change', this.getEventValue(event))
    },
    getEventValue(event) {
      const value = isObject(event)
        ? event.target.value
        : event

      return this.filterOut
        ? this.filterOut(value)
        : value
    },
    is(...types) {
      return types.some(type => this.type === type)
    },
    set(event) {
      const value = this.getEventValue(event)
      this.$emit('update:value', value)
      this.$emit('input', value)
    },
    setSelectIcon(value) {
      this.selectIcon = value
    },
    setFocus(value) {
      this.isFocused = value
    },
  },
}
</script>

<style lang="scss">
$_switch-background: $success;

.Field-cont label,
label.Field-label {
  @include trans;
  color: inherit;
  display: block;
  font-size: $h5;
  font-weight: $semibold;
  position: relative;
  @include md {
    font-size: inherit;
    line-height: inherit;
  }

  &.has-errors {
    margin-bottom: 0;
  }
}

// TODO: remove stylelint disable
/* stylelint-disable no-descending-specificity */
input[type="number"] {
  -moz-appearance: textfield;

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.Field {
  input,
  select,
  textarea,
  input[type="number"],
  &.with-icon {
    background: $white;
    border: 1px solid $border-color;
    border-radius: $radius-lg;
    outline: none;
    width: 100%;
  }

  input,
  select,
  textarea,
  input[type="number"] {
    font-size: inherit;
    line-height: inherit;
  }

  textarea {
    line-height: 1.5rem;
    min-height: 200px;
  }

  input[type="number"] {
    padding-right: 0;
  }

  input,
  select,
  option,
  textarea {
    @include trans;
    padding: 0.25rem 0.5rem;

    &.success {
      border-color: $success;
    }

    &.info {
      border-color: $info;
    }

    &:active,
    &:focus {
      background: $white;
      border-color: $info;
      box-shadow: $shadow-lg-info;
    }
  }

  .checkbox {
    color: $text-color;
    cursor: pointer;
    display: inline-block;

    input {
      width: auto;
    }

    .slider {
      display: none;
    }

    &.disabled {
      color: $text-color-light;
      cursor: not-allowed;

      label {
        color: $text-color-lighter;
      }
    }
  }

  .checkbox .switch {
    display: inline-block;
    position: relative;

    label {
      height: 100%;
    }

    input {
      height: 0;
      opacity: 0;
      width: 2.5rem;

      &:checked {
        + .slider {
          background-color: $_switch-background;
          box-shadow: 0 0 1px $_switch-background;

          &::before {
            -webkit-transform: translateX(1rem);
            -ms-transform: translateX(1rem);
            transform: translateX(1rem);
          }
        }
      }
    }

    .slider {
      @include trans(0.2s);
      background-color: $grey-light;
      border-radius: $radius-round;
      bottom: 0;
      cursor: pointer;
      display: block;
      height: 1.5rem;
      left: 0;
      line-height: inherit;
      position: absolute;
      right: 0;
      top: 0.5rem;
      width: 2.5rem;

      &::before {
        @include trans(0.2s);
        background-color: $white-bis;
        border-radius: $radius-round;
        bottom: 0.25rem;
        content: "";
        height: 1rem;
        left: 0.25rem;
        position: absolute;
        width: 1rem;
      }
    }
  }

  &.focus {
    border-color: $info;
    box-shadow: 0 0 2px $info-light;
  }

  &.type-checkbox,
  &.type-radio {
    @include clearfix;

    input {
      margin-right: 0.5rem;
    }

    label {
      cursor: pointer;
    }
  }

  &.type-radio {
    margin-bottom: 0;
  }

  &.type-select {
    label {
      line-height: 1rem;
      margin: 0.75rem 0 0;
      padding-left: 0.75rem;
    }
  }

  &.with-icon {
    padding-left: 3rem;
    padding-right: 0;
    position: relative;

    input,
    select {
      border: none;

      &:active,
      &:focus {
        border-color: transparent;
        box-shadow: none;
      }
    }

    > .Icon {
      border-right: 1px solid $border-color;
      font-size: $h4;
      font-weight: $regular;
      left: 0;
      line-height: inherit;
      padding: 0.225rem 1rem 0.25rem;
      position: absolute;
      top: 0;
    }

    // modifiers
    &.trans {
      > .Icon {
        background: transparent;
        border: none;
      }
    }

    &.icon-right {
      padding-left: 0;
      padding-right: 2.75rem;

      > .Icon {
        border-right: 0;
        left: auto;
        right: 0;
      }
    }

    &.type-select {
      padding-left: 0;
      padding-right: 0;

      > .Icon {
        left: auto;
        padding: 0.375rem 0.5rem;
        pointer-events: none;
        position: absolute;
        right: 0;
      }

      select {
        padding-right: 2rem;
      }
    }
  }

  // state
  &.disabled {
    input {
      background: $white-bis;
    }
  }

  &.valid {
    input,
    &.with-icon {
      border-color: $success;

      &:focus {
        box-shadow: $shadow-lg-success;
      }
    }
  }

  &.invalid {
    margin-bottom: 0;

    input,
    &.with-icon {
      border-color: $danger;

      &:focus {
        box-shadow: $shadow-lg-danger;
      }
    }
  }
}
/* stylelint-enable no-descending-specificity */

label span.required {
  color: $danger;
}

.Field-cont {
  font-size: $h5;
  line-height: $lh;
  margin-bottom: 1rem;
  @include md {
    font-size: $h5;
    line-height: 2.5rem;
  }

  .Field-errors {
    color: $danger;
    line-height: 1.5rem;
  }
}
</style>
